<template>
  <b-modal id="import-games-modal" size="xl" title="Import games" @ok="clearAndHide" ok-title="Close" ok-only>
    <div>
      <h1 class="mb-0">Import games</h1>
      <p class="mt-4">This feature allows you to import games and their media from content in a ZIP file.</p>
      <p>
        The ZIP file must contain a
        <code>games.txt</code>
        file that is base64 encoded, as well as the modified media in the
        <code>assets/</code>
        folder.
      </p>
      <b-alert show variant="info">
        <strong>Important - Media files:</strong>
        <ul class="mb-0 mt-2">
          <li>Only media files that are referenced in the games will be imported</li>
          <li>Any media files in the ZIP that are not used by any game will be ignored</li>
        </ul>
      </b-alert>
      <div class="d-flex mt-4">
        <b-form-file
          ref="fileInput"
          accept=".zip"
          placeholder="Choose a ZIP file"
          :disabled="importLoading"
          @change="handleFileChange"></b-form-file>
      </div>
      <b-progress :value="uploadProgress" :max="100" class="mt-4">
        <b-progress-bar
          :value="uploadProgress"
          :label="uploadProgressLabel"
          show-progress
          :animated="!isProcessing"
          :variant="uploadProgress < 100 ? 'primary' : 'success'"></b-progress-bar>
      </b-progress>
    </div>

    <b-alert :show="!!validationError" variant="danger" class="mt-3">
      <h6 class="mb-2">
        <i class="fa fa-exclamation-triangle mr-2"></i>
        {{ (validationError && validationError.message) || 'Error' }}
      </h6>

      <!-- Détails génériques pour tous types d'erreurs -->
      <div v-if="hasErrorDetails()" class="mt-2">
        <!-- Validation ZIP côté client (cas spécial) -->
        <div v-if="validationError.missingFiles && validationError.missingFiles.length > 0">
          <strong>Missing files at root:</strong>
          <ul class="mt-1 mb-2">
            <li v-for="file in validationError.missingFiles" :key="file">
              <code>{{ file }}</code>
            </li>
          </ul>
        </div>

        <div v-if="validationError.rootFiles && validationError.rootFiles.length > 0">
          <strong>Files found at root:</strong>
          <ul class="mt-1 mb-2">
            <li v-for="file in validationError.rootFiles" :key="file">
              <code>{{ file }}</code>
            </li>
          </ul>
        </div>

        <div v-if="validationError.rootFolders && validationError.rootFolders.length > 0">
          <strong>Folders found at root:</strong>
          <ul class="mt-1 mb-2">
            <li v-for="folder in validationError.rootFolders" :key="folder">
              <code>{{ folder }}/</code>
              <small class="text-muted ml-1">(required files may be in this folder)</small>
            </li>
          </ul>
        </div>

        <!-- Détails backend génériques -->
        <div v-if="validationError.details">
          <!-- En-têtes manquants -->
          <div v-if="validationError.details.missing_headers && validationError.details.missing_headers.length > 0">
            <strong>Missing headers:</strong>
            <ul class="mt-1 mb-2">
              <li v-for="header in validationError.details.missing_headers" :key="header">
                <code>{{ header }}</code>
              </li>
            </ul>
          </div>

          <!-- Erreurs de validation express-validator -->
          <div v-if="validationError.details.validationErrors">
            <strong>Details:</strong>
            <ul class="mt-1 mb-2">
              <li v-for="(msg, field) in validationError.details.validationErrors" :key="field">
                <strong>{{ field }}</strong>
                : {{ msg }}
              </li>
            </ul>
          </div>

          <!-- Erreur technique -->
          <div v-if="validationError.details.originalError" class="mt-2">
            <small class="text-muted">
              <strong>Technical details:</strong>
              <code>{{ validationError.details.originalError }}</code>
            </small>
          </div>
        </div>
      </div>
    </b-alert>

    <b-alert :show="!!successMessage" variant="success" class="mt-3" dismissible @dismissed="successMessage = null">
      <h6 class="mb-2">
        <i class="fa fa-check-circle mr-2"></i>
        Import successful
      </h6>
      {{ successMessage }}
    </b-alert>

    <b-alert :show="importLoading" variant="info" class="mt-1 d-flex flex-column align-items-center">
      <b-spinner variant="primary" label="Loading..." class="mb-2" />
      <span v-if="isProcessing">Processing file. Please wait...</span>
      <span v-else>Uploading file. Please wait...</span>
    </b-alert>
  </b-modal>
</template>

<script>
  import axios from 'axios'
  import { validateGamesZip } from '../../utils/zipValidator'
  import ChunkedUploadService from '../../services/chunkedUpload.service'

  export default {
    name: 'ImportGamesModal',
    props: {
      language: {
        type: String,
        required: true,
      },
    },
    computed: {
      jwt() {
        return localStorage.getItem('jwt')
      },
      uploadProgressLabel() {
        return `${this.uploadProgress.toFixed(2)}%`
      },
    },
    data() {
      return {
        // Upload
        uploadProgress: 0,
        importLoading: false,
        currentChunk: 0,
        totalChunks: 0,
        isProcessing: false,
        // Validation
        validationError: null,
        successMessage: null,
      }
    },
    methods: {
      show() {
        this.$bvModal.show('import-games-modal')
      },
      hide() {
        this.$bvModal.hide('import-games-modal')
      },
      async clearFile() {
        this.uploadProgress = 0
        this.importLoading = false
        this.currentChunk = 0
        this.totalChunks = 0
        this.isProcessing = false
        this.validationError = null
        this.successMessage = null
        this.$refs.fileInput.reset()
      },
      clearAndHide() {
        this.clearFile()
        this.hide()
      },
      async handleFileChange(event) {
        const fileToUpload = event.target.files[0]

        if (!fileToUpload) {
          this.$notify({
            type: 'error',
            title: 'Error',
            message: 'Please select a ZIP file',
          })
          return
        }

        // Validation du contenu du ZIP avant upload
        try {
          this.importLoading = true
          this.uploadProgress = 0
          this.currentChunk = 0
          this.totalChunks = 0
          this.validationError = null

          // Étape 1: Validation du contenu du ZIP
          const validation = await validateGamesZip(fileToUpload)

          if (!validation.isValid) {
            this.validationError = validation
            this.emitFailure()
            return
          }

          // Étape 2: Upload par chunks si la validation passe
          const uploadService = new ChunkedUploadService({
            chunkSize: 10 * 1024 * 1024, // 10 MB par chunk
            maxRetries: 3,
            retryDelay: 1000,
          })

          const uploadResult = await uploadService.uploadFile(fileToUpload, {
            jwt: this.jwt,
            additionalData: {
              language: this.language,
            },
            onProgress: (progress, currentChunk, totalChunks) => {
              this.uploadProgress = progress
              this.currentChunk = currentChunk
              this.totalChunks = totalChunks
            },
          })

          // Étape 3: Upload terminé à 100%, passer en mode "traitement"
          this.uploadProgress = 100
          this.isProcessing = true

          // Étape 4: Appeler le controller d'import pour traiter le fichier fusionné
          await axios.post(
            '/api/games/import',
            {
              filePath: uploadResult.data.filePath,
              language: this.language,
            },
            {
              withCredentials: true,
              headers: {
                Authorization: 'Bearer ' + this.jwt,
                'Content-Type': 'application/json',
              },
            }
          )

          this.$notify({
            type: 'success',
            title: 'Success',
            message: 'Games have been imported successfully',
          })

          this.successMessage = 'Games have been imported successfully'
          this.emitSuccess()
        } catch (error) {
          console.log(error)

          this.processError(error)
          this.emitFailure()
        } finally {
          this.importLoading = false
        }
      },
      processError(error) {
        let errorMessage = 'An error occurred'

        // 1. Erreurs express-validator (422 avec format { errors: {...} })
        if (error.response?.status === 422 && error.response.data?.errors) {
          this.handleValidationErrors(error.response.data.errors)
          return
        }

        // 2. Nouveau format unifié ({ success: false, error: {...} })
        if (error.response?.data?.error) {
          const errorData = error.response.data.error
          errorMessage = errorData.message || 'An error occurred'

          // Stockage simple des données backend
          this.validationError = {
            isValid: false,
            message: errorMessage,
            details: errorData.details || {},
            code: errorData.code,
          }
        }
        // 3. Ancien format ou erreur inattendue
        else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
          this.validationError = {
            isValid: false,
            message: errorMessage,
            details: {},
          }
        }
        // 4. Erreur réseau ou autre
        else {
          this.validationError = {
            isValid: false,
            message: 'Connection error or unexpected error',
            details: {},
          }
        }

        // Notification toast
        this.$notify({
          type: 'error',
          title: 'Import error',
          message: errorMessage,
        })
      },
      handleValidationErrors(errors) {
        // Traiter les erreurs express-validator
        const errorMessages = []
        const errorDetails = {}

        Object.keys(errors).forEach((field) => {
          const fieldError = errors[field]
          errorMessages.push(`${field}: ${fieldError.msg}`)
          errorDetails[field] = fieldError.msg
        })

        const mainMessage = 'Validation errors'

        this.validationError = {
          isValid: false,
          message: mainMessage,
          details: {
            validationErrors: errorDetails,
            fields: Object.keys(errors),
          },
        }

        // Notification avec le premier message d'erreur
        this.$notify({
          type: 'error',
          title: 'Validation error',
          message: errorMessages[0] || mainMessage,
        })
      },
      hasErrorDetails() {
        if (!this.validationError) return false

        // Vérifier s'il y a des détails à afficher
        return (
          // Validation ZIP côté client
          (this.validationError.missingFiles && this.validationError.missingFiles.length > 0) ||
          (this.validationError.rootFiles && this.validationError.rootFiles.length > 0) ||
          (this.validationError.rootFolders && this.validationError.rootFolders.length > 0) ||
          // Détails backend
          (this.validationError.details && Object.keys(this.validationError.details).length > 0)
        )
      },
      emitSuccess() {
        this.$emit('success')
      },
      emitFailure() {
        this.$emit('failure')
      },
    },
  }
</script>
